import React, { useEffect, useState } from "react";
import "./Login.css";
import Checkbox from "@mui/material/Checkbox";
import {
  Box,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Grid from "@mui/material/Grid";
import Div from "@jumbo/shared/Div";
import LoadingButton from "@mui/lab/LoadingButton";
import authServices, { BASE_URL } from "../../../services/auth-services";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";
import Cookies from "js-cookie";
import itemServices from "app/services/item-master-services";
import { handleError } from "./AuthGuard";
import { useDispatch } from 'react-redux';
import { fetchDateFormater } from "app/redux/actions/fetchDateFormater";
import { loginSuccess } from "app/redux/authSlice";
import { MdOutlineRefresh } from "react-icons/md";
import { ErpStatusConfirmDialogBox } from "app/shared/ReuseComponents/ErpDialogBoxes";


const generateCaptcha = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let captcha = '';
  for (let i = 0; i < 6; i++) {
    captcha += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return captcha;
};


const Login = () => {
  const navigate = useNavigate();
  //  redux code
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState('');
  const [errors, setErrors] = useState({});

  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
    rememberMe: false,
  });
  const [isLoading, setIsLoading] = useState(false);


  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    setCredentials((prevCredentials) => ({ ...prevCredentials, [name]: val }));
  };


  // Retrieve the email and password from localStorage when the component mounts
  useEffect(() => {
    const username = Cookies.get('username');
    const password = Cookies.get('password');
    if (username && password) {
      setCredentials({
        username: username,
        password: password,
        rememberMe: true, // Set this to true as you're auto-filling from cookies
      });
    }
  }, [])


  const validateForm = () => {
    const newErrors = {};

    if (credentials?.username === undefined || credentials?.username === null || credentials?.username === "") {
      newErrors.username = "User Name is required.";
    }
    if (credentials?.password === undefined || credentials?.password === null || credentials?.password === "") {
      newErrors.password = "Password is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const onSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (captcha !== captchaInput) {
      toast.error('CAPTCHA does not match. Please try again.');
      setCaptchaInput("");
      setCaptcha(generateCaptcha());
      setIsLoading(false);
      return;
    }

    const { username, password, rememberMe } = credentials;
    if (!validateForm()) {
      toast.error("Enter valid username and password.");
      navigate("/");
      return;
    }

    try {
      // Step 1: Fetch user by username
      const userRes = await axios.get(`${BASE_URL}/Felix/Usersignup/?username=${username}`);
      const userData = userRes?.data;
      setLocalStorage(userData);

      // Step 2: Fetch employee data by email
      const empRes = await axios.get(`${BASE_URL}/Felix/getemployeeeid/?email=${userData?.email}`);
      const empData = empRes?.data;

      const isUser = userData?.UserType === "User";

      // Step 3: Access control checks for normal users
      if (isUser) {
        const isRestricted =
          userData?.Is_Deleted ||
          !empData?.length ||
          empData[0]?.Is_Deleted ||
          empData[0]?.Abscand_Flg ||
          empData[0]?.Resigned_Flg ||
          empData[0]?.Dismissed_Flg;

        if (isRestricted) {
          toast.error("The user hasn't permitted access for this application.");
          navigate("/");
          return;
        }

        if (userData?.User_Login_Status) {
          toast.error("You are currently signed in on another device.");
          navigate("/");
          setTimeout(() => setOpenResetDialog(true), 1000);
          return;
        }
      }

      // Step 4: Perform login
      const loginRes = await authServices.signIn(username, password, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      const token = loginRes?.data?.access;
      localStorage.setItem("accesstoken", token);

      // Step 5: Update login flag
      const loginFlagRes = await axios.put(
        `${BASE_URL}/Felix/UpdateUserLoginFlagTrue/`,
        { id: userData?.id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const userDetails = loginFlagRes?.data;
      setLocalStorage(userDetails);

      // Step 6: Redux login
      dispatch(loginSuccess({ username, password }));
      dispatch(fetchDateFormater());

      // Step 7: Remember Me cookies
      rememberMe ? setCredentialsCookies(username, password) : removeCredentialsCookies();

      toast.success("Successful Login");

      // Step 8: Post-login setup
      // if (isUser) {
      await fetchPartnerId(userDetails?.Business_Unit, token);
      await fetchHeadApproverName(token, userData?.Organization_Id, userData?.email);
      await fetchCompanyDatas(userData?.Organization_Id, token);
      // }

      // Step 9: Navigate
      navigate(userDetails?.UserType === "User" ? "/home" : "/user-admin-dashboard");
    } catch (error) {
      handleError(error);
      navigate("/");
    } finally {
      setIsLoading(false);
    }

  };

  // Additional utility functions for clarity

  const setLocalStorage = (data) => {
    localStorage.setItem("UserDetails", JSON.stringify(data));
    localStorage.setItem("UserId", data?.id);
    localStorage.setItem("OrganizationId", data?.Organization_Id);
    localStorage.setItem("Username", `${data?.first_name} ${data?.last_name}`);
    localStorage.setItem("Designation", data?.Designation);
  };

  const setCredentialsCookies = (username, password) => {
    const expirationPeriod = 365 * 100;
    Cookies.set('username', username, { expires: expirationPeriod });
    Cookies.set('password', password, { expires: expirationPeriod });
  };

  const removeCredentialsCookies = () => {
    Cookies.remove('username');
    Cookies.remove('password');
  };

  const fetchPartnerId = async (businessUnit, token) => {
    try {
      const res = await axios.get(`${BASE_URL}/Felix/PartnerListreportDropdown/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      const partnerLists = res?.data?.filter(opt => opt?.Partner_Name === businessUnit);
      localStorage.setItem("PartnerId", partnerLists[0]?.id);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchHeadApproverName = async (token, companyId, email) => {
    try {
      const headResponse = await itemServices.getEmployees(email, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      const head = headResponse[0]

      localStorage.setItem("EmployeeId", head?.id);
      localStorage.setItem("EmployeeNo", head?.Employee_Id);

      const reportingTodata = await axios.get(`${BASE_URL}/Felix/GetEmployeeWithoutToken/?Employee_Id=${head?.Reportingto_Id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      if (reportingTodata?.data) {
        const reportingHead = reportingTodata?.data;
        console.log("reportingHead", reportingHead)
        localStorage.setItem("ReportHead", reportingHead?.id);
        localStorage.setItem("ReportHeadName", `${reportingHead?.Employee_FirstName} ${reportingHead?.Employee_LasttName}`);
        localStorage.setItem("ReportHeadRole", reportingHead?.Designation);
      }

    } catch (error) {
      console.error(error);
    }
  };

  const fetchCompanyDatas = async (companyId, token) => {
    try {
      const response = await axios.get(`${BASE_URL}/Felix/CompanyCRUD/?id=${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      const companyData = response?.data;
      localStorage.setItem("DateFormat", companyData?.DateFormat);
      localStorage.setItem("CurrencyFormat", companyData?.Currencies);

    } catch (error) {
      console.error('Error fetching company data:', error);
    }
  }


  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };


  const handleResetLoginStatus = async () => {
    const userspayload = {
      id: parseInt(localStorage.getItem("UserId")) || 2
    }
    await axios
      .put(`${BASE_URL}/Felix/UpdateUserLoginFlagFalse/`, userspayload)
      .then((res) => {
        console.log(res.data);
        if (res?.data?.id) {
          localStorage.removeItem("accesstoken");
          localStorage.clear();
          navigate("/");
          toast.success("Logged out successfully.")
          setOpenResetDialog(false);
        }
      })
      .catch((error) => {
        console.log("ds", error);
      });
  }


  return (
    <>
      <ToastContainer autoClose={2000} />

      <div className="login_container">
        <Paper elevation={4}>
          <div id="main-wrapper" className="oxyy-login-register">
            <div className="container-fluid login_child_container px-0">
              <div className="row g-0 min-vh-100">
                <div className="col-md-6">
                  <div className="hero-wrap d-flex align-items-center h-100">
                    <div className="hero-mask"></div>
                    <div className="hero-bg hero-bg-scroll"></div>
                  </div>
                </div>
                <div className="col-md-6 d-flex">
                  <div className="container my-auto py-0">
                    <div className="row g-0">
                      <div className="col-11 col-sm-10 col-lg-9 col-xl-8 mx-auto">
                        <div style={{ textAlign: '-webkit-center' }}>
                          <img
                            // src="./images/Felix.png"
                            src="./images/HRMFarmlogo.png"
                            width={175}
                            height={55}
                            className="login-logo-image"
                            alt="logo"
                          />
                        </div>
                        <Typography variant="h2" className="fw-600 mb-3">Log In</Typography>
                        <form onSubmit={onSignIn}>
                          <Div sx={{ mt: 2, mb: 3 }}>
                            <label htmlFor="Username" className="form-label">Username</label>
                            <input className="form-control"
                              id="username"
                              type="text"
                              name="username"
                              value={credentials?.username}
                              onChange={handleInputChange}
                              placeholder="Enter your username"
                              autoComplete="off"
                              required
                              style={{ borderRadius: "5px !important" }}
                            />
                            <Div style={{ color: "red", fontSize: "12px" }}>
                              {!credentials?.username && errors?.username}
                            </Div>
                          </Div>
                          <Div sx={{ mt: 1, mb: 3 }}>
                            <label htmlFor="password" className="form-label" id="loginPassword">Password</label>
                            <div className="input-group">
                              <input
                                className="form-control"
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={credentials?.password}
                                onChange={handleInputChange}
                                placeholder="Enter Your Password"
                                autoComplete="off"
                                required
                                style={{ borderRadius: "5px !important" }}
                              />
                              <div
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                style={{
                                  position: "absolute",
                                  right: "10px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  border: "none",
                                  background: "none",
                                  cursor: "pointer",
                                  zIndex: 999,
                                }}
                              >
                                {showPassword ? <VisibilityOff style={{ fontSize: 18, color: "#579ddf", padding: "0px !imoportant" }} />
                                  : <Visibility style={{ fontSize: 18, color: "#579ddf", padding: "0px !imoportant" }} />
                                }
                              </div>
                            </div>

                            <Div style={{ color: "red", fontSize: "12px" }}>
                              {!credentials?.password && errors?.password}
                            </Div>
                          </Div>

                          <Div
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              flexDirection: { xs: "row", sm: "row", md: "row" },
                            }}
                          >
                            <Div className="d-flex">
                              <Checkbox
                                type="checkbox"
                                size="small"
                                className="col-12 form-check-input"
                                id="rememberme"
                                name="rememberMe"
                                checked={credentials?.rememberMe}
                                onChange={handleInputChange}
                              />
                              <Typography variant="h5" sx={{ pl: 1, pt: 0 }}>
                                Remember Me
                              </Typography>
                            </Div>

                            <Typography className="fgt-pwd-text" variant={"body1"}>
                              <Typography
                                variant="h5"
                                color="primary"
                                onClick={() => navigate("/auth-pages/forgot-password")}
                                sx={{ cursor: "pointer" }}
                              >
                                Forgot Password?
                              </Typography>
                            </Typography>
                          </Div>

                          <Grid container columnSpacing={1} rowSpacing={1}>
                            <Grid item xs={12} md={12}>
                              <Box sx={{ backgroundColor: "#ECECEC", padding: 0.75, textAlign: "center", borderRadius: "4px" }}>
                                <Typography variant="h3"
                                  color={"#000000"}
                                  sx={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    letterSpacing: 2,
                                    userSelect: 'none',
                                    '&::after': {
                                      content: '""',
                                      position: 'absolute',
                                      top: '50%',
                                      left: -5,
                                      width: '110%',
                                      height: '1.1px', // Adjust the height of the strikethrough line as needed
                                      backgroundColor: 'rgb(69, 71, 75, 0.7)', // White with 50% opacity
                                      transform: 'translateY(-50%)',
                                      pointerEvents: 'none', // Ensure the line doesn't interfere with text interaction
                                    },
                                  }}
                                >{captcha}</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={12}>
                              <div className="input-group">
                                <input
                                  className="form-control"
                                  name="captcha"
                                  value={captchaInput}
                                  onChange={(e) => setCaptchaInput(e.target.value)}
                                  placeholder="Enter Captcha"
                                  autoComplete="off"
                                  required
                                  style={{ borderRadius: "5px !important" }}
                                />
                                <div
                                  style={{
                                    position: "absolute",
                                    right: "10px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    border: "none",
                                    background: "none",
                                    cursor: "pointer",
                                    zIndex: 999,
                                  }}
                                >
                                  <IconButton
                                    aria-label="toggle refresh"
                                    onClick={() => {
                                      setCaptcha(generateCaptcha());
                                      setCaptchaInput("");
                                    }}
                                    edge="end"
                                    sx={{ p: 1.5 }}
                                  >
                                    <MdOutlineRefresh size={22} color="#017890" />
                                  </IconButton>
                                </div>
                              </div>
                            </Grid>
                          </Grid>

                          <LoadingButton
                            fullWidth
                            type="submit"
                            className="col-sm-12 col-md-12"
                            size="large"
                            variant="contained"
                            // color="primary"
                            sx={{
                              mt: 2,
                              cursor: "pointer",
                            }}
                          >
                            {isLoading ? "Loading..." : "Sign In"}

                          </LoadingButton>
                          <br />
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Paper>
      </div>

      <ErpStatusConfirmDialogBox
        flag={openResetDialog}
        setFlag={setOpenResetDialog}
        handleClick={handleResetLoginStatus}
        content={"You are currently signed in on another device. For security reasons, you can only be signed in on one device at a time. Would you like to log out of all devices and continue here?"}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          marginTop: '20px',  // Add margin to avoid it being at the very top
        }}
      />
    </>
  );
};

export default Login;
